/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         1/02/24 - 11:26
 * Module name:  menu.service
 * File name:    menu-manager.service.ts
 * IDE:          WebStorm
 */

import {DestroyRef, inject, Injectable} from '@angular/core';
import {NbMenuBag, NbMenuItem, NbMenuService, NbSidebarService} from '@nebular/theme';
import {
  MkadMenuItem,
  MENU_MAIN_CONST,
  MENU_MAIN_TAG,
  MENU_OVERLAY_TAG,
  MENU_USER_OVERLAY,
  NO_INDEX_FIND,
  SIDEBAR_MAIN_ID,
  MENU_ITEM_OVERVIEW,
  MENU_ITEM_CUSTOMERS_ADMIN,
  MENU_ITEM_PARTNERS,
  MENU_ITEM_CONTRACTS,
  SESSION_MENU_ITEMS_CONST,
  MAIN_MENU_ITEM_REPORT_DETAIL,
  MAIN_MENU_ITEM_HOME,
} from '../../constants';
import {BehaviorSubject, Subject} from 'rxjs';
import {MenuComponentConfigEnum, MenuComponentOverlayEnum} from '../../enums';
import {TranslateService} from '@ngx-translate/core';
import {UserLevelEnum} from '@zibanu/auth';
import {CustomerLevelEnum} from '@shared/interface';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PreferencesService} from '@shared/services';

@Injectable()
export class MkadMenuManagerService {
  private readonly translate = inject(TranslateService);
  private readonly nbMenu = inject(NbMenuService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly preferences = inject(PreferencesService);
  private readonly sidebarService = inject(NbSidebarService);
  private mainMenu: NbMenuItem[] = [];
  private overlayMenu: NbMenuItem[] = [];
  readonly mainMenu$ = new BehaviorSubject<NbMenuItem[]>(this.mainMenu);
  readonly overlayMenu$ = new BehaviorSubject<NbMenuItem[]>(this.overlayMenu);
  private readonly logout$$ = new Subject<null>();
  private readonly change$$ = new Subject<null>();
  private readonly about$$ = new Subject<null>();
  private readonly userSetting$$ = new Subject<null>();
  private readonly appSetting$$ = new Subject<null>();

  /**
   * @description
   * Constructor class
   */
  constructor() {
    this.onClickMenuListener();
  }

  /**
   * @description
   * Method that refresh the items in the main sidebar menu
   * @param userLevel current user level
   * @param clientLevel current user level
   * @param isDiamondCustomer _flag - is user from customer diamond level loggediNN
   */
  reloadMainMenu(userLevel: number, clientLevel: CustomerLevelEnum, isDiamondCustomer?: boolean): void {
    const localMainMenu: MkadMenuItem[] = structuredClone(MENU_MAIN_CONST);
    const mainMenuFiltered = localMainMenu.filter((item) => {
      const {data} = <MkadMenuItem>item;

      if (userLevel === UserLevelEnum.Guest) {
        return userLevel === data.level;
      }

      if (clientLevel === CustomerLevelEnum.final) {
        return (data.clientLevel === null || data.clientLevel === CustomerLevelEnum.final) && userLevel <= data.level;
      } else {
        return (data.clientLevel === null || data.clientLevel === CustomerLevelEnum.silver) && userLevel <= data.level;
      }
    });
    const itemIndex = mainMenuFiltered.findIndex((i) => i.data.id === MENU_ITEM_PARTNERS);
    if (itemIndex > NO_INDEX_FIND) {
      const children: MkadMenuItem[] | undefined = mainMenuFiltered[itemIndex].children;
      if (children) {
        const newChildren = children.map((x): MkadMenuItem => ({
          ...x,
          title: this.translate.instant(x.title),
        }));
        mainMenuFiltered[itemIndex].children = [...newChildren];
      }
    }
    if (!isDiamondCustomer) {
      const administrationIndex = mainMenuFiltered.filter(x => x.data.id !== MENU_ITEM_CUSTOMERS_ADMIN);
      this.setMainMenu(this.getTranslateMap(administrationIndex, ['title']));
    } else {
      const menuMainCloned: MkadMenuItem[] = structuredClone(MENU_MAIN_CONST);
      const menuMainClonedFiltered = menuMainCloned.filter(x => x.data.id === MAIN_MENU_ITEM_REPORT_DETAIL || x.data.id === MAIN_MENU_ITEM_HOME);
      const itemToShow = menuMainClonedFiltered.concat(mainMenuFiltered).sort((a, b) => Number(a.data.id) - Number(b.data.id));
      this.setMainMenu(this.getTranslateMap([...itemToShow], ['title']));
    }
    this.mainMenu$.next(this.mainMenu);
    this.reloadOverlayMenu(isDiamondCustomer ? CustomerLevelEnum.diamond : clientLevel);
  }

  reloadMenuForVoyeur(isFinalClient: boolean): void {
    const overviewItemIndex = this.mainMenu.findIndex(x => x.data.id === MENU_ITEM_OVERVIEW);
    const newMenuItems: NbMenuItem[] = [];
    if (overviewItemIndex > NO_INDEX_FIND) {
      newMenuItems.push(this.mainMenu[overviewItemIndex]);
    }

    if (!isFinalClient) {
      const partnerItemIndex = this.mainMenu.findIndex(x => x.data.id === MENU_ITEM_PARTNERS);
      if (partnerItemIndex > NO_INDEX_FIND) {
        newMenuItems.push(this.mainMenu[partnerItemIndex]);
      }
    }

    this.setMainMenu(this.getTranslateMap(
      newMenuItems.concat(SESSION_MENU_ITEMS_CONST),
      ['title'])
    );
    this.mainMenu$.next(this.mainMenu);
  }

  removeContractInMenu(): void {
    const currenItems = structuredClone(this.mainMenu);
    const contractIndex = currenItems.findIndex(x => x.data.id === MENU_ITEM_CONTRACTS);
    if (contractIndex > NO_INDEX_FIND) {
      currenItems.splice(contractIndex, 1);
      this.setMainMenu(this.getTranslateMap(currenItems, ['title']));
      this.mainMenu$.next(this.mainMenu);
    }
  }

  /**
   * @description
   * Method that sets the new items for the overlay menu
   * @param menu
   * @private
   */
  private setNewOverlayMenu(menu: NbMenuItem[]): void {
    this.setOverlayMenu(this.getTranslateMap([...menu], ['title']));
    this.overlayMenu$.next(this.overlayMenu);
  }

  /**
   * @description
   * Method that refresh the items in the overlay menu
   */
  reloadOverlayMenu(clientLevel: CustomerLevelEnum): void {
    const menuUserOverlay = structuredClone(MENU_USER_OVERLAY);
    const partnersMenu = clientLevel === CustomerLevelEnum.diamond
      ? [...menuUserOverlay]
      : menuUserOverlay.filter(x => x.data.component !== MenuComponentOverlayEnum.AppPreferences);
    const overlayMenuNewItems: NbMenuItem[] = clientLevel !== CustomerLevelEnum.final
      ? partnersMenu
      : structuredClone(menuUserOverlay);
    this.setNewOverlayMenu(overlayMenuNewItems);
  }

  /**
   * @description
   * Method that valid the click event in the item of the sidebar
   */
  private onClickMenuListener(): void {
    this.nbMenu.onItemClick()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (itemPressed: NbMenuBag) => {
          const {item, tag} = itemPressed;
          if (!item.data) return;
          if (tag === MENU_MAIN_TAG) {
            if (this.preferences.autoHideSidebar) {
              this.sidebarService.compact(SIDEBAR_MAIN_ID);
            }
            if (item.data.component == MenuComponentConfigEnum.MkadLogoutComponent) this.logout$$.next(null);
            if (item.data.component == MenuComponentConfigEnum.MkadAboutUsComponent) this.about$$.next(null);
          } else if (tag === MENU_OVERLAY_TAG) {
            if (item.data.component == MenuComponentOverlayEnum.UserProfile) this.userSetting$$.next(null);
            if (item.data.component == MenuComponentOverlayEnum.AppPreferences) this.appSetting$$.next(null);
            if (item.data.component == MenuComponentOverlayEnum.ChangePassword) this.change$$.next(null);
            if (item.data.component == MenuComponentOverlayEnum.Logout) this.logout$$.next(null);
          }
        },
      });
  }

  /**
   * @description
   * Method that set the data for the main menu
   * @param newMenu new data to set
   */
  private setMainMenu(newMenu: NbMenuItem[]): void {
    this.mainMenu = newMenu;
  }

  /**
   * @description
   * Method that set the data for the overlay menu
   * @param newMenu new data to set
   */
  private setOverlayMenu(newMenu: NbMenuItem[]): void {
    this.overlayMenu = newMenu;
  }

  /**
   * @description
   * Method that returns any change in the main menu
   */
  onChangeMainMenu() {
    return this.mainMenu$;
  }

  /**
   * @description
   * Method that returns any change in the overlay menu
   */
  onChangeOverlayMenu() {
    return this.overlayMenu$;
  }


  /**
   * @description
   * Method that return the logout observable
   */
  onClickLogout() {
    return this.logout$$;
  }

  /**
   * @description
   * Method that return the user setting observable
   */
  onClickUserSetting() {
    return this.userSetting$$;
  }

  /**
   * @description
   * Method that return the app setting observable
   */
  onClickAppSetting() {
    return this.appSetting$$;
  }

  /**
   * @description
   * Method that return the about us observable
   */
  onClickAboutUs() {
    return this.about$$;
  }

  /**
   * @description
   * Method that return the change password observable
   */
  onClickChangePassword() {
    return this.change$$;
  }

  /**
   * @description
   * Method that translates the menu dynamically
   * @param list list you want to translate
   * @param keyToTranslate keys of the object to be translated
   */
  getTranslateMap<T = object>(list: T[], keyToTranslate: string[]) {
    list.forEach((item: T, itemIndex: number): void => {
      keyToTranslate.forEach((keyItem: string): void => {
        this.translate.get(item[keyItem as keyof T] as string).subscribe((result: string): void => {
          list[itemIndex][keyItem as keyof T] = result as T[keyof T];
        });
      });
    });
    return list;
  }
}
